// @flow
import React from 'react';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';
import { TextBlock, Text, toastError } from 'bench-internal-theme';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
// $FlowFixMe - TS file
import { SplitFeatureFlags } from '../../features';
import LedgerAccountsTableHeader from './LedgerAccountsTableHeader';
import LedgerAccountsTableRowContainer from './LedgerAccountsTableRowContainer';
import type { LedgerAccount, LedgerCategory } from '../types';
import { showServerError } from 'bk-web-app';
import { useMutation } from '@apollo/client';
import {
  GET_LEDGER_ACCOUNTS,
  GET_LEDGER_ACCOUNTS_BY_CLIENT,
  REORDER_LEDGER_ACCOUNT,
} from '../queries';
// $FlowFixMe - TS file
import { apolloV2Client } from '../../../general-ledger/queries';
import { useClient } from '../../ClientContext';
import { useDrop } from 'react-dnd';
import EmptyTableRow from '../EmptyTableRow';
import { compareAsNumbers, compareAsStrings } from 'comparators';

const LedgerAccountsTableEmpty = styled(TextBlock)`
  align-items: start;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.silt};
  box-shadow: 0 -1px 0 ${colors.silt};
  margin: 0 32px;
  padding: 8px;
  position: relative;
`;

const LedgerAccountsTableLayout = styled.div`
  margin: 0 -32px;
  padding: ${({ isSorting }) => (isSorting ? `32px 0` : `24px 0`)};
`;
type LedgerAccountsTableProps = {
  ledgersAccountsForCategory: Array<LedgerAccount>,
  search: string,
  ledgerCategory: LedgerCategory,
  isSorting: boolean,
  canSort: boolean,
  isCategorySortedAlphabetical: boolean,
  isLoading: boolean,
};

const LedgerAccountsTable = ({
  ledgersAccountsForCategory,
  search,
  ledgerCategory,
  isSorting,
  canSort,
  isCategorySortedAlphabetical,
  isLoading,
}: LedgerAccountsTableProps) => {
  const client = useClient();

  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;

  const isAssignCategoryImprovementEnabled =
    features[SplitFeatureFlags.ASSIGN_CATEGORY_IMPROVEMENT]?.value === FeatureValue.FEATURE_ON;

  const ledgerAccountsQuery = isAssignCategoryImprovementEnabled
    ? GET_LEDGER_ACCOUNTS_BY_CLIENT
    : GET_LEDGER_ACCOUNTS;

  const [reorderLedgerAccount, { loading }] = useMutation(REORDER_LEDGER_ACCOUNT, {
    onError: error => {
      toastError('An error occurred reordering this ledger account', showServerError(error));
    },
    onCompleted: async () => {
      if (isAssignCategoryImprovementEnabled) {
        await apolloV2Client.query({
          query: GET_LEDGER_ACCOUNTS_BY_CLIENT,
          variables: { clientId: client.id },
          context: { useV2: true },
          fetchPolicy: 'network-only',
        });
      }
    },
    awaitRefetchQueries: true,
    refetchQueries: isAssignCategoryImprovementEnabled
      ? []
      : [{ query: GET_LEDGER_ACCOUNTS, variables: { clientId: client.id } }],
  });

  const changeOrder = (ledgerAccountId, reorderId, isLast = false) =>
    reorderLedgerAccount({ variables: { ledgerAccountId, reorderId, isLast } });

  const sortLedgersByOrderNumber = () =>
    Array.from(ledgersAccountsForCategory.values()).sort((ledgerAccountA, ledgerAccountB) =>
      compareAsNumbers(
        ledgerAccountA.sortOrder ?? ledgerAccountA.order,
        ledgerAccountB.sortOrder ?? ledgerAccountB.order,
      ),
    );

  const sortLedgersAlphabetical = () =>
    Array.from(ledgersAccountsForCategory.values()).sort((ledgerAccountA, ledgerAccountB) =>
      compareAsStrings(ledgerAccountA.name, ledgerAccountB.name),
    );

  const sortedLedgerAccounts = isCategorySortedAlphabetical
    ? sortLedgersAlphabetical()
    : sortLedgersByOrderNumber();

  const lastLedgerId = sortedLedgerAccounts[sortedLedgerAccounts.length - 1]?.id;

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: `${ledgerCategory.name}-row`,
      canDrop: () => isSorting && canSort,
      drop: monitor => changeOrder(monitor.id, lastLedgerId, true),
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [lastLedgerId, isSorting, canSort],
  );

  return (
    <LedgerAccountsTableLayout isSorting={isSorting}>
      <LedgerAccountsTableHeader />
      {sortedLedgerAccounts.length ? (
        <>
          {sortedLedgerAccounts.map(ledgerAccount => (
            <LedgerAccountsTableRowContainer
              changeOrder={changeOrder}
              isSortSaving={loading || isLoading}
              key={ledgerAccount.id}
              canSort={canSort}
              ledgerCategory={ledgerCategory}
              ledgerAccount={ledgerAccount}
              search={search}
              isSorting={isSorting}
            />
          ))}
          {/* Allows user to drop row at the bottom of the table to sort last */}
          {isSorting && canSort && canDrop && <EmptyTableRow ref={drop} isOver={isOver} />}
        </>
      ) : (
        <LedgerAccountsTableEmpty>
          <Text styleType="small" color={colors.cloud}>
            No ledgers found for this category
          </Text>
        </LedgerAccountsTableEmpty>
      )}
    </LedgerAccountsTableLayout>
  );
};

export default LedgerAccountsTable;
